import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cookie from 'js-cookie';
import { Text, Spacing } from '@reservamos/elements';
import ModalPopUp from 'ui/atoms/ModalPopUp';
import LocalizedCurrency from '../../../LocalizedCurrency';
import {
  trackDiscountModalOpened,
  trackDiscountModalClosed,
  trackDiscountModalNewSearch,
} from '../../../../metrics/user-analytics/search';
import discountETN from '../../../../images/brands-unique/etn/discountETN.png';
import discountGFA from '../../../../images/brands-unique/gfa/early-discount.png';

const COOKIE_NAME = 'isDiscountModalOpened';

const iconTypes = {
  etn: discountETN,
  gfa: discountGFA,
  default: 'emojiSparkles',
};

/**
 * This component is used to display the discount modal.
 * @param {Object} props
 * @param {boolean} props.isRoundTrip - Whether the search is a round trip.
 * @param {number} props.anticipation - The anticipation of the search.
 * @param {number} props.percent - The percent of the discount.
 * @param {number} props.minPrice - The minimum price of the search.
 * @param {function} props.goToAnticipationSearch - The function to go to the anticipation search.
 * @param {number} props.price - The price of the search.
 * @param {string} props.search - The search.
 * @param {Object} props.analyticSearchData - The analytic search data.
 */
const ModalContentDiscount = ({
  isRoundTrip,
  anticipation,
  percent,
  minPrice,
  goToAnticipationSearch,
  price,
  search,
  analyticSearchData,
}) => {
  const { env } = useSelector((state) => state.whitelabelConfig);
  const [visible, setVisible] = useState(false);

  /**
   * This function is used to show the modal.
   */
  const showModal = () => setVisible(true);

  useEffect(() => {
    const isPreviouslyOpened = Boolean(cookie.get(COOKIE_NAME));

    if (!isPreviouslyOpened) {
      showModal();
      trackDiscountModalOpened(analyticSearchData);
      cookie.set(COOKIE_NAME, true, { expires: 7 });
    }
  }, [analyticSearchData]);

  if (isRoundTrip) return null;

  const title = `Disfruta hasta ${percent}% de descuento`;

  /**
   * This function is used to close the modal.
   * And track the discount modal closed event.
   */
  const closeModal = () => {
    setVisible(false);
    trackDiscountModalClosed(analyticSearchData);
  };

  if (!visible) return null;

  return (
    <ModalPopUp
      modalTitle={title}
      modalIconType={iconTypes[env.brand] ?? iconTypes.default}
      buttonPrimaryText="Compra con anticipación"
      buttonSecondaryText="No estoy interesado"
      onClickPrimary={() => {
        trackDiscountModalNewSearch(analyticSearchData);
        goToAnticipationSearch(search);
      }}
      onClickSecondary={() => closeModal()}
    >
      <Spacing vertical size="S">
        <Text fontFamily="secondary" size="S">
          Compra con hasta {anticipation} días de anticipación y obtén hasta un
          <strong> {percent}%</strong> de descuento en tu viaje. Aplica restricción de horarios.
        </Text>
        <Text fontFamily="secondary" size="S">
          Precio por adulto de tu búsqueda:{' '}
          <LocalizedCurrency price={minPrice} currencyStyle="currency" decimals={0} weight="bold" />
        </Text>
        <Text fontFamily="secondary" size="S">
          Precio por adulto al planificar tu viaje:{' '}
          <LocalizedCurrency price={price} currencyStyle="currency" decimals={0} weight="bold" />
        </Text>
        <Text fontFamily="secondary" size="S">
          Tu ahorro:{' '}
          <LocalizedCurrency
            price={minPrice - price}
            currencyStyle="currency"
            decimals={0}
            weight="bold"
          />
        </Text>
      </Spacing>
    </ModalPopUp>
  );
};

ModalContentDiscount.propTypes = {
  isRoundTrip: PropTypes.bool.isRequired,
  minPrice: PropTypes.number.isRequired,
  percent: PropTypes.number,
  price: PropTypes.number,
  search: PropTypes.string,
  anticipation: PropTypes.number.isRequired,
  goToAnticipationSearch: PropTypes.func.isRequired,
  analyticSearchData: PropTypes.shape({
    destination: PropTypes.shape({
      cityName: PropTypes.string,
      cityId: PropTypes.string,
      id: PropTypes.string,
    }),
    origin: PropTypes.shape({
      cityName: PropTypes.string,
      cityId: PropTypes.string,
      id: PropTypes.string,
    }),
  }),
};

export default ModalContentDiscount;
